import { observable, configure, action, computed } from 'mobx';
import LocalModel from './localModel';

import util from 'preact-util';
import { route } from 'preact-router';
import PubSub, { topics } from '../lib/pubsub';

import { MapContainer, TileLayer, Marker, Popup, Polyline, Polygon, useMap, GeoJSON, latLngBounds } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from 'leaflet';

// import isoCountries from 'iso-countries';

import markerSvg from '../assets/mapicons/marker.svg';
import markerCircleSvg from '../assets/mapicons/circle-marker.svg';
import markerDogSvg from '../assets/mapicons/dog-duotone.svg';

import localUtil from '../lib/util';

configure({ enforceActions: 'always' });

// Function to calculate offset positions in a circular pattern
function getCirclePoints(center, initialRadius, count, radiusIncrement) {
    var points = [];
    var skewAngle = 45 * Math.PI / 180; // Convert 45 degrees to radians

    for (var i = 0; i < count; i++) {
        var radius = initialRadius + (radiusIncrement * i); // Increase radius for each point
        var angle = (i / count) * (2 * Math.PI) + skewAngle; // Add skewAngle to rotate
        // console.log({ angle });
        var dx = radius * Math.cos(angle);
        var dy = radius * Math.sin(angle);
        // console.log({ center });
        points.push([center.lat + dy, center.long + dx]);
    }
    return points;
}

function areMarkersClose(marker1, marker2, proximityInMeters) {
    const earthRadiusInMeters = 6371000;

    const dLat = degreesToRadians(marker2.lat - marker1.lat);
    const dLon = degreesToRadians(marker2.long - marker1.long);

    const lat1 = degreesToRadians(marker1.lat);
    const lat2 = degreesToRadians(marker2.lat);

    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.sin(dLon/2) * Math.sin(dLon/2) * Math.cos(lat1) * Math.cos(lat2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const distance = earthRadiusInMeters * c;

    return distance <= proximityInMeters;
}

function degreesToRadians(degrees) {
    return degrees * Math.PI / 180;
}

function isMarker1FurtherNorth(marker1, marker2) {
    if (marker1 && marker2) {
        return marker1.lat > marker2.lat;
    }
}

function getRotationDegree(tIdx = 1) {
    const idx = tIdx + 1; // Add 1 to avoid 0
    // Define the range and step
    const range = 20; // -30 to +30 is a range of 60
    const step = 3;  // Change in degrees for each step

    // Calculate the number of steps within the range
    const stepsInRange = range / step;

    // Use modulo operator to cycle through steps within the range
    const normalizedIndex = tIdx % stepsInRange;

    // Calculate the rotation degree
    const rotationDegree = normalizedIndex * step;

    return rotationDegree;
}

class GpsPointStore extends LocalModel {
    constructor() {
        super('gpsPoint', {
            namePlural: 'gpsPoints',
            namePluralReal: 'gpsPoints',
            sort: 'timestamp',
            limit: 500,
            api: {
                search: {
                    url: '/api/gpspoints/',
                    params: {
                        limit: 15,
                        sort: 'timestamp',
                    },
                },
                load: {
                    url: '/api/gpspoints/',
                    params: {},
                },
                save: {
                    url: '/api/gpspoints/',
                    params: {},
                },
                delete: {
                    url: '/api/gpspoints/',
                    params: {},
                },
            },
        });
    }

    @observable newGpsPoint = {};

    @observable gpsPoint = {};

    @observable lastPoint = { lat: 62.573821, lng: 11.3857662 };

    @observable gpsPoints = [];

    @observable races = [];

    @observable polyline = [];

    @observable trackers = [];

    @observable totalTrackLength = 0;

    @observable bounds = [];

    @observable markers = [];

    @observable lines = [];

    @observable allGpsDevices = [];

    @observable weather = {};

    // Client-side cache for YR weather API calls to prevent excessive requests
    // Uses geohash (~5km precision) with 15-minute TTL
    weatherCache = new Map();
    CACHE_TTL = 15 * 60 * 1000; // 15 minutes

    /**
     * Generate a simple geohash for ~5km precision caching
     * @param {number} lat - Latitude
     * @param {number} lon - Longitude
     * @returns {string} - Geohash string
     */
    getGeohash(lat, lon) {
        // Round to ~5km precision (0.05 degrees ≈ 5km at equator)
        return `${Math.round(lat * 20)}:${Math.round(lon * 20)}`;
    }

    @action
    addWeather = (key, value) => {
        this.weather[key] = value;
    }

    @action
    setLastPoint() {
        const { gpsPoints } = this;
        if (gpsPoints.length > 0) {
            const lastPoint = gpsPoints[gpsPoints.length - 1];
            this.lastPoint = lastPoint;
        }
    }

    @action
    setPolyline() {
        const { gpsPoints } = this;
        if (gpsPoints.length > 0) {
            const polyline = gpsPoints.map(point => [point.lat, point.lng]);
            this.polyline = polyline;
        }
    }

    @action
    setBounds() {
        const { trackers } = this;
        this.bounds = [];
        if (trackers.length > 0) {
            trackers.forEach(tracker => {
                // Get last point from each tracker
                // const lastPoint = tracker.points[tracker.points.length - 1];
                // this.bounds.push([lastPoint[0], lastPoint[1]]);
                // const lastPoint = tracker.point;
                const lastPoint = [tracker.lat, tracker.long];
                // this.bounds.push([lastPoint[1], lastPoint[0]]);
                this.bounds.push([lastPoint[0], lastPoint[1]]);
                // tracker.points.forEach(point => {
                    // this.bounds.push([point[0], point[1]]);
                // });
            });
        }
    }

    @action
    setMarkers() {
        const { trackers } = this;
        this.markers = [];
        this.lines = [];
        const processedTrackers = new Set(); // To keep track of processed trackers

        if (trackers.length > 0) {
            trackers.forEach((tracker, tIdx) => {
                if (processedTrackers.has(tracker.trackerId)) {
                    return;
                }

                const currentTracker = { ...tracker };
                const currentContestant = currentTracker.contestant || { firstname: 'trackerId', lastname: currentTracker.trackerId };
                const lastPoint = [currentTracker.lat, currentTracker.long];

                // // Check for overlapping markers
                // const overlappingMarkers = trackers.filter(m =>  {
                //     return m.lat === currentTracker.lat && m.long === currentTracker.long;
                // });

                // Check for overlapping markers
                const overlappingMarkers = trackers.filter((m, mIdx) => {
                    if (processedTrackers.has(m.trackerId)) {
                        return false;
                    }
                    return m.trackerId !== currentTracker.trackerId && areMarkersClose(m, currentTracker, 30);
                });
                const closeMarkers = trackers.filter((m, mIdx) => {
                    if (processedTrackers.has(m.trackerId)) {
                        return false;
                    }
                    return m.trackerId !== currentTracker.trackerId && areMarkersClose(m, currentTracker, 400);
                });
                currentTracker.closeMarkers = closeMarkers;

                // Constants
                const lineHeight = '1.1em';
                const iconSize = [150,30];
                const iconLabelWidth = 120;
                const iconAnchorRight = [3,15];
                const iconAnchorLeft = [147, 15];

                if (overlappingMarkers.length > 0) {
                    const circlePoints = getCirclePoints(currentTracker, 0.0004, overlappingMarkers.length, 0.00002); // Adjust radius as needed
                    // console.log({ circlePoints })
                    circlePoints.forEach((point, idx) => {
                        const overlappingMarker = overlappingMarkers[idx];
                        const overlappingContestant = overlappingMarker.contestant || { firstname: 'trackerId', lastname: overlappingMarker.trackerId };

                        // Create offset marker
                        const offsetMarker = new L.divIcon({
                            className: 'leaflet-marker-icon leaflet-div-icon tracker-marker-icon',
                            iconUrl: markerDogSvg,
                            iconRetinaUrl: markerDogSvg,
                            popupAnchor:  [0, -15],
                            iconAnchor: [3,9],
                            iconSize,
                            html: `
                            <div
                                class='d-flex flex-row justify-content-start align-items-center'
                                style=''
                            >
                                <div class='rounded-circle me-1' style='width: 7px; height: 7px; background-color: ${currentTracker.bgColor || '#ff0000'};'></div>
                                <div class='bg-white rounded-pill px-2' style='font-size: 11px;'>
                                    <nobr>
                                        ${localUtil.getFlagSvgString(overlappingContestant.country, 15, true) || ''}
                                        <span style='color: #555;' class='mx-1'>${overlappingContestant.bib}</span>
                                        <span style='color: #333;'>${overlappingContestant.lastname}</span>
                                    </nobr>
                                </div>
                            </div>`,
                        });
                        overlappingMarker.icon = offsetMarker;
                        overlappingMarker.isSpread = true;
                        this.markers.push([point[0], point[1], overlappingMarker]);
                        processedTrackers.add(overlappingMarker.trackerId);
                        const iconMrk = new L.divIcon({
                            className: 'leaflet-marker-icon leaflet-div-icon tracker-marker-icon',
                            iconUrl: markerSvg,
                            iconRetinaUrl: markerSvg,
                            popupAnchor:  [0, -15],
                            iconAnchor: [8,8],
                            iconSize: [15,15],
                            html: `
                            <div
                                class='d-flex flex-row justify-content-start align-items-center'
                            >
                                <div class='rounded-circle' style='width: 17px; height: 17px; background-color: ${currentTracker.bgColor || '#ff0000'}; border: 2px solid #ffffff;'></div>
                            </div>`,
                        });
                        currentTracker.icon = iconMrk;
                        // Draw line
                        // var line = L.polyline([[data.lat, data.lng], [point[0], point[1]]], { color: 'red' });
                        this.lines.push([currentTracker.lat, currentTracker.long, point[0], point[1]]);
                    });
                    this.markers.push([lastPoint[0], lastPoint[1], currentTracker]);
                    processedTrackers.add(currentTracker.trackerId);
                } else {
                    const isNorth = isMarker1FurtherNorth(currentTracker, closeMarkers[0]);
                    let rotateDeg = getRotationDegree(currentTracker.trackerId);

                    // console.log(currentTracker);
                    let iconPosition = 'right';
                    const bearing = currentTracker.bearing || 0;
                    if (bearing > 270) {
                        iconPosition = 'left';
                    } else if (bearing > 0 && bearing < 90) {
                        iconPosition = 'left';
                    }

                    // Calculate time since last GPS point in seconds
                    const timeSinceLastGpsPoint = parseInt((new Date().getTime() - new Date(currentTracker.timestamp).getTime()) / 1000, 10);

                    // Choose icon based on time since last gps point
                    let lastUpdateIcon;
                    if (timeSinceLastGpsPoint > 3600) {
                        lastUpdateIcon = `fa-duotone fa-wifi-exclamation text-danger`;
                    } else if (timeSinceLastGpsPoint > 1800) {
                        lastUpdateIcon = `fa-duotone fa-wifi-exclamation text-warning`;
                    }

                    // Choose type icon based on currentTracker.type
                    let typeIcon;
                    if (currentTracker.type === 'gsm') {
                        typeIcon = `fa-solid fa-phone text-white`;
                    } else if (currentTracker.type === 'sat') {
                        typeIcon = `fa-solid fa-satellite-dish text-white`;
                    }

                    // Format track distance for display (convert from meters to km)
                    const trackDistance = currentTracker.totalDistanceOnTrack;
                    const distanceStr = trackDistance != null ? `${(trackDistance / 1000).toFixed(1)} km` : '';

                    const iconMrk = new L.divIcon({
                        className: 'leaflet-marker-icon leaflet-div-icon tracker-marker-icon',
                        iconUrl: markerSvg,
                        iconRetinaUrl: markerSvg,
                        popupAnchor:  [0, -15],
                        iconAnchor: iconPosition === 'right' ? iconAnchorRight : iconAnchorLeft,
                        iconSize,
                        html: iconPosition === 'right' ? `
                            <div
                                class='d-flex flex-row justify-content-start align-items-center w-100'
                                style='${closeMarkers && closeMarkers.length > 0 ? `transform-origin: left bottom; transform: rotate(${isNorth ? -rotateDeg : rotateDeg}deg);` : ''}'
                            >
                                <div
                                    class='d-flex align-items-center justify-content-center overflow-hidden rounded-circle me-1'
                                    style='width: 15px !important; min-width: 15px; height: 15px; background-color: ${currentTracker.bgColor || '#ff0000'}; border: 2px solid #ffffff; font-size: 7px;'
                                >
                                    ${typeIcon ? `<i class='${typeIcon}'></i>` : ''}
                                </div>
                                <div class='bg-white rounded-pill px-2 border' style='font-size: 11px;'>
                                    <div class='d-flex flex-row align-items-center'>
                                        <div class='d-flex flex-column justify-content-center align-items-center me-1'>
                                            ${currentTracker.bearing || lastUpdateIcon ? `<small class='font-weight-light' style='line-height: ${lineHeight}; color: #555;'>
                                                <i
                                                    class='${lastUpdateIcon ? lastUpdateIcon : 'fa-duotone fa-arrow-up'}'
                                                    style='${!lastUpdateIcon ? `transform: rotate(${currentTracker.bearing}deg);` : ''}'
                                                ></i>
                                            </small>` : ''}
                                            ${currentTracker.avgSpeed > 0.5 && !lastUpdateIcon ? `<small class='font-weight-light text-nowrap' style='line-height: ${lineHeight}; color: #555;'>${currentTracker.avgSpeed.toFixed(0)} km/t</small>` : ''}
                                        </div>
                                        <div class='d-flex flex-row text-truncate align-items-center overflow-hidden ms-1' style='max-width: ${iconLabelWidth}px;'>
                                            ${currentContestant.country ? localUtil.getFlagSvgString(currentContestant.country, 15, true) || '' : ''}
                                            <span style='color: #555;' class='mx-1'>${currentContestant.bib}</span>
                                            <span style='color: #333;'>${currentContestant.lastname}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ` : `
                            <div
                                class='d-flex flex-row justify-content-end align-items-center w-100'
                                style='${closeMarkers && closeMarkers.length > 0 ? `transform-origin: right bottom; transform: rotate(${isNorth ? -rotateDeg : rotateDeg}deg);` : ''}'
                            >
                                <div class='bg-white rounded-pill px-2 border' style='font-size: 11px;'>
                                    <div class='d-flex flex-row align-items-center'>
                                        <div class='d-flex flex-row text-truncate align-items-center overflow-hidden me-1' style='max-width: ${iconLabelWidth}px;'>
                                            <span style='color: #555;' class='me-1'>${currentContestant.bib}</span>
                                            <span style='color: #333;' class='me-1'>${currentContestant.lastname}</span>
                                            ${currentContestant.country ? localUtil.getFlagSvgString(currentContestant.country, 15, true) || '' : ''}
                                        </div>
                                        <div class='d-flex flex-column justify-content-center align-items-center ms-1'>
                                            ${currentTracker.bearing || lastUpdateIcon ? `<small class='font-weight-light' style='line-height: ${lineHeight}; color: #555;'>
                                                <i
                                                    class='${lastUpdateIcon ? lastUpdateIcon : 'fa-duotone fa-arrow-up'}'
                                                    style='${!lastUpdateIcon ? `transform: rotate(${currentTracker.bearing}deg);` : ''}'
                                                ></i>
                                            </small>` : ''}
                                            ${currentTracker.avgSpeed > 0.5 && !lastUpdateIcon ? `<small class='font-weight-light text-nowrap' style='line-height: ${lineHeight}; color: #555;'>${currentTracker.avgSpeed.toFixed(0)} km/t</small>` : ''}
                                        </div>
                                    </div>
                                </div>
                                <div
                                    class='d-flex align-items-center justify-content-center overflow-hidden rounded-circle ms-1'
                                    style='width: 15px !important; min-width: 15px; height: 15px; background-color: ${currentTracker.bgColor || '#ff0000'}; border: 2px solid #ffffff; font-size: 7px;'
                                >
                                    ${typeIcon ? `<i class='${typeIcon}'></i>` : ''}
                                </div>
                            </div>

                        `,
                    });
                    currentTracker.icon = iconMrk;
                    // Get last point from each tracker
                    // const lastPoint = tracker.points[tracker.points.length - 1];
                    // const lastPoint = tracker.point;
                    // this.markers.push([lastPoint[1], lastPoint[0], tracker]);
                    this.markers.push([lastPoint[0], lastPoint[1], currentTracker]);
                    processedTrackers.add(currentTracker.trackerId);
                }
            });
            // console.log({ processedTrackers });
        }
    }

    @action
    updateTrackerFromFirebase(data) {
        if (!data || !data.lat || !data.lng || !data.trackerId) {
            return false;
        }
        const trackerIdx = this.trackers.findIndex(t => t.trackerId === data.trackerId);
        if (trackerIdx === -1) {
            // Unknown tracker — caller should fall back to full API load
            return false;
        }
        const tracker = { ...this.trackers[trackerIdx] };
        // Update latest position
        tracker.lat = data.lat;
        tracker.long = data.lng;
        tracker.speed = data.speed || tracker.speed || 0;
        tracker.battery = data.battery || tracker.battery || 0;
        tracker.elevation = data.elevation || tracker.elevation || 0;
        tracker.timestamp = data.timestamp || new Date().toISOString();
        tracker._firebaseUpdateTime = Date.now();

        // Append new point to the tracker's points array
        if (tracker.points && Array.isArray(tracker.points)) {
            tracker.points.push([data.lat, data.lng]);
        }

        // Replace tracker in array
        const updatedTrackers = [...this.trackers];
        updatedTrackers[trackerIdx] = tracker;
        this.trackers = updatedTrackers;

        // Update map markers
        this.setMarkers();
        return true;
    }

    async getGpsPoints({ race, raceClass, cutoffDate = '', replay = false }) {
        // Prevent concurrent identical API calls from racing each other.
        // Dedupe only identical requests — a replay load must not be swallowed
        // by an in-flight live load (or a different race/raceClass).
        const requestKey = `${race}|${raceClass}|${cutoffDate}|${replay}`;
        if (this._gpsLoadingPromise && this._gpsLoadingKey === requestKey) {
            return this._gpsLoadingPromise;
        }
        this._gpsLoadingKey = requestKey;
        this._gpsLoadingPromise = this._doGetGpsPoints({ race, raceClass, cutoffDate, replay });
        try {
            return await this._gpsLoadingPromise;
        } finally {
            if (this._gpsLoadingKey === requestKey) {
                this._gpsLoadingPromise = null;
                this._gpsLoadingKey = null;
            }
        }
    }

    async _doGetGpsPoints({ race, raceClass, cutoffDate = '', replay = false }) {
        // Clear markers immediately when in replay mode to prevent stale data during loading
        let replaySeq;
        if (replay) {
            this._replayRequestSeq = (this._replayRequestSeq || 0) + 1;
            replaySeq = this._replayRequestSeq;
            this.updateKeyValue('markers', []);
            this.updateKeyValue('lines', []);
        }
        const replayParam = replay ? '&replay=true' : '';
        const response = await util.fetchApi(`/api/gpspoints/group?race=${race}&raceClass=${raceClass}&cutoffDate=${cutoffDate}${replayParam}`, {  method: 'GET' }, { });
        switch (response.status) {
            case 200: {
                if (replay) {
                    // A newer replay request was issued while this one was in
                    // flight (rapid class switch) — drop this stale response
                    if (replaySeq !== this._replayRequestSeq) {
                        return false;
                    }
                    // Replay needs the full authoritative history. Never merge with
                    // live/firebase trackers — their timestamps are often newer than
                    // the replay data's last point, which made the merge below keep
                    // the existing trackers and silently discard the entire replay
                    // payload (winner=existing for every tracker → hasChanges=false).
                    this.updateKeyValue('trackers', response.data);
                    this.updateKeyValue('totalTrackLength', response.included?.totalTrackLength || 0);
                    // Markers/bounds are computed by the replay renderer, not setMarkers()
                    return true;
                }
                const apiTrackerMap = new Map(response.data.map(t => [t.trackerId, t]));
                let hasChanges = false;
                const mergedTrackers = this.trackers.map(existingTracker => {
                    const apiTracker = apiTrackerMap.get(existingTracker.trackerId);
                    if (!apiTracker) {
                        console.log(`[GPS Merge] source=localAPI trackerId=${existingTracker.trackerId} winner=existing (not in API)`);
                        return existingTracker;
                    }
                    // Compare timestamps: keep existing position if it's newer or equal to API data (NaN-safe)
                    const existingTs = existingTracker.timestamp ? new Date(existingTracker.timestamp).getTime() : 0;
                    const apiTs = apiTracker.timestamp ? new Date(apiTracker.timestamp).getTime() : 0;
                    if (!isNaN(existingTs) && existingTs > 0 && (isNaN(apiTs) || existingTs >= apiTs)) {
                        // Existing tracker (from Firebase) has newer or equal data — don't regress
                        console.log(`[GPS Merge] source=localAPI trackerId=${apiTracker.trackerId} existingTs=${existingTs} apiTs=${apiTs} winner=existing`);
                        return existingTracker;
                    }
                    hasChanges = true;
                    console.log(`[GPS Merge] source=localAPI trackerId=${apiTracker.trackerId} existingTs=${existingTs} apiTs=${apiTs} winner=api`);
                    if (existingTracker.points && existingTracker.points.length > (apiTracker.points || []).length) {
                        return { ...existingTracker, ...apiTracker, points: existingTracker.points };
                    }
                    return { ...existingTracker, ...apiTracker };
                });
                // Add API-only trackers (new trackers not yet in existing)
                response.data.forEach(apiTracker => {
                    if (!this.trackers.find(t => t.trackerId === apiTracker.trackerId)) {
                        console.log(`[GPS Merge] source=localAPI trackerId=${apiTracker.trackerId} winner=api (no existing)`);
                        mergedTrackers.push(apiTracker);
                        hasChanges = true;
                    }
                });
                console.log(`[GPS Merge] source=localAPI trackerCount before=${this.trackers.length} after=${mergedTrackers.length} hasChanges=${hasChanges}`);
                if (hasChanges) {
                    this.updateKeyValue('trackers', mergedTrackers);
                    this.updateKeyValue('totalTrackLength', response.included?.totalTrackLength || 0);
                    // Skip marker updates for replay mode - markers are computed differently during replay
                    if (!replay) {
                        this.setBounds();
                        this.setMarkers();
                    }
                }
                return true;
                break;
            }
            default:
                return false;
                break;
        }
    }

    async getGpsDeviceStatus({ cutoffDate = '' }) {
        const response = await util.fetchApi(`/api/gpspoints/status?cutoffDate=${cutoffDate}`, {  method: 'GET' }, { });
        switch (response.status) {
            case 200:
                this.updateKeyValue('allGpsDevices', response.data);
                return true;
                break;
            default:
                return false;
                break;
        }
    }

    async getYrWeather({ lat, lon, altitude = 500 }) {
        if (!lat || !lon) return null;

        // Check client-side cache first
        const geohash = this.getGeohash(lat, lon);
        const cached = this.weatherCache.get(geohash);

        if (cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
            return cached.data;
        }

        // Fetch from API
        const result = await util.fetchApi('/api/yr/public', { method: 'GET' }, { lat, lon, altitude });

        // Cache the result
        this.weatherCache.set(geohash, { data: result, timestamp: Date.now() });

        return result;
    }

    async getYrWeatherHistorical({ lat, lon, timestamp }) {
        if (lat && lon && timestamp) {
            const result = await util.fetchApi('/api/yr/historical', { publish: true, method: 'GET' }, { lat, lon, timestamp });
            return result;
        }
        return null;
    }

    async getRaceWeatherSummary({ raceId }) {
        if (raceId) {
            const result = await util.fetchApi('/api/yr/race-summary', { publish: true, method: 'GET' }, { raceId });
            return result;
        }
        return null;
    }

    async getConventionWeatherSummary({ conventionId }) {
        if (conventionId) {
            const result = await util.fetchApi('/api/yr/convention-summary', { publish: true, method: 'GET' }, { conventionId });
            return result;
        }
        return null;
    }
}

const store = new GpsPointStore();
export default store;
